import { TrimmedOfferLocations } from 'components/TrimmedOfferLocations';
import { useI18n } from 'hooks/useI18n';
import { UIOfferHeroSection } from 'interfaces/ui/ui-offer-hero-section';
import { Badge } from '../../shared/Badge';
import {
  StyledHeroInfoData,
  StyledHeroInfoDataBadges,
  StyledHeroInfoDataItem,
  StyledHeroInfoDataItemGroup,
  StyledHeroInfoDataItems,
  StyledHeroInfoDataItemSeparator,
  StyledHeroInfoDataItemTitle,
  StyledHeroInfoDataItemValue
} from './HeroInfoData.styled';

const HeroInfoData = (props: UIOfferHeroSection) => {
  const { t } = useI18n('job-offer');

  const salaryMin = props.salaryMin || null;
  const salaryMax = props.salaryMax || null;
  const hasLocations = props.jobLocationsSummary.trimmedCount > 0;
  const isRemote = props.jobRemote === 100;
  const isFreelance = props.isFreelance;
  const hourlyRateFrom = props.hourlyRateFrom || 0;
  const hourlyRateTo = props.hourlyRateTo || 0;

  return (
    <StyledHeroInfoData>
      <StyledHeroInfoDataItems>
        <StyledHeroInfoDataItem className="is-salary">
          <StyledHeroInfoDataItemTitle>
            {isFreelance ? t('hero.fee') : t('hero.salary')}
          </StyledHeroInfoDataItemTitle>

          {isFreelance ? (
            <StyledHeroInfoDataItemValue to="oferta" href="#oferta">
              <strong>
                {!!hourlyRateTo && !hourlyRateFrom && (
                  <span>{t('hero.up_to')} </span>
                )}
                {!!hourlyRateFrom && !hourlyRateTo && (
                  <span>{t('hero.from')} </span>
                )}
                {!!hourlyRateFrom &&
                  hourlyRateFrom !== hourlyRateTo &&
                  hourlyRateFrom}
                {!!hourlyRateFrom &&
                  hourlyRateFrom !== hourlyRateTo &&
                  !!hourlyRateTo && <span>-</span>}
                {!!hourlyRateTo && hourlyRateTo}
                {props.currency}
              </strong>
              <span>/{t('common:salary.per_hour')}</span>
            </StyledHeroInfoDataItemValue>
          ) : (
            <StyledHeroInfoDataItemValue to="oferta" href="#oferta">
              {!salaryMin && salaryMax && (
                <>
                  <span>{t('hero.up_to')} </span>
                </>
              )}

              {salaryMin && !salaryMax && (
                <>
                  <span>{t('hero.from')} </span>
                </>
              )}

              <span>{props.currency}</span>

              {salaryMin && salaryMax && salaryMin !== salaryMax && (
                <>
                  <strong>{salaryMin}</strong>-<strong>{salaryMax}</strong>
                </>
              )}

              {salaryMin && salaryMax && salaryMin === salaryMax && (
                <>
                  <strong>{salaryMax}</strong>
                </>
              )}

              {!salaryMin && salaryMax && <strong>{salaryMax}</strong>}

              {salaryMin && !salaryMax && <strong>{salaryMin}</strong>}

              <span>K</span>
            </StyledHeroInfoDataItemValue>
          )}
        </StyledHeroInfoDataItem>

        {props.bonus && (
          <StyledHeroInfoDataItem className="is-salary">
            <StyledHeroInfoDataItemTitle>
              {t('hero.bonus')}
            </StyledHeroInfoDataItemTitle>
            <StyledHeroInfoDataItemValue to="oferta" href="#oferta">
              <span className="extra-value">+</span>
              <span>{props.currency}</span>
              <strong>{props.bonus}</strong>
              <span>K</span>
            </StyledHeroInfoDataItemValue>
          </StyledHeroInfoDataItem>
        )}

        <StyledHeroInfoDataItemGroup>
          {props.jobRemote === 100 ? (
            <>
              <StyledHeroInfoDataItem>
                <StyledHeroInfoDataItemTitle>
                  {isRemote ? t('hero.remote') : t('hero.remotework')}
                </StyledHeroInfoDataItemTitle>
                <StyledHeroInfoDataItemValue to="donde" href="#donde">
                  {props.jobRemote}%
                </StyledHeroInfoDataItemValue>
              </StyledHeroInfoDataItem>

              {hasLocations && props.jobRemote < 100 && props.jobRemote > 0 && (
                <StyledHeroInfoDataItemSeparator>
                  {t('hero.and')}
                </StyledHeroInfoDataItemSeparator>
              )}

              {hasLocations && isRemote && (
                <StyledHeroInfoDataItemSeparator>
                  {t('hero.or')}
                </StyledHeroInfoDataItemSeparator>
              )}

              {hasLocations && (
                <StyledHeroInfoDataItem>
                  <StyledHeroInfoDataItemTitle>
                    {t('hero.presential')}
                  </StyledHeroInfoDataItemTitle>

                  <StyledHeroInfoDataItemValue to="donde" href="#donde">
                    <TrimmedOfferLocations
                      locationsSummary={props.jobLocationsSummary}
                    />
                  </StyledHeroInfoDataItemValue>
                </StyledHeroInfoDataItem>
              )}
            </>
          ) : (
            <>
              {hasLocations && (
                <StyledHeroInfoDataItem>
                  <StyledHeroInfoDataItemTitle>
                    {t('hero.presential')}
                  </StyledHeroInfoDataItemTitle>

                  <StyledHeroInfoDataItemValue to="donde" href="#donde">
                    <TrimmedOfferLocations
                      locationsSummary={props.jobLocationsSummary}
                    />
                  </StyledHeroInfoDataItemValue>
                </StyledHeroInfoDataItem>
              )}

              {hasLocations &&
                props.jobRemote !== 0 &&
                props.jobRemote < 100 &&
                props.jobRemote > 0 && (
                  <StyledHeroInfoDataItemSeparator>
                    {t('hero.and')}
                  </StyledHeroInfoDataItemSeparator>
                )}

              {hasLocations && props.jobRemote !== 0 && isRemote && (
                <StyledHeroInfoDataItemSeparator>
                  {t('hero.or')}
                </StyledHeroInfoDataItemSeparator>
              )}

              {props.jobRemote !== 0 && (
                <StyledHeroInfoDataItem>
                  <StyledHeroInfoDataItemTitle>
                    {isRemote ? t('hero.remote') : t('hero.remotework')}
                  </StyledHeroInfoDataItemTitle>
                  <StyledHeroInfoDataItemValue to="donde" href="#donde">
                    {props.jobRemote}%
                  </StyledHeroInfoDataItemValue>
                </StyledHeroInfoDataItem>
              )}
            </>
          )}
        </StyledHeroInfoDataItemGroup>
      </StyledHeroInfoDataItems>

      {props.badges && (
        <StyledHeroInfoDataBadges>
          {props.badges.map((b) => (
            <Badge
              key={b.id}
              variant={`${b.highlight ? 'primaryInverse' : 'secondaryInverse'}`}
            >
              {b.item}
            </Badge>
          ))}
        </StyledHeroInfoDataBadges>
      )}
    </StyledHeroInfoData>
  );
};

export default HeroInfoData;
